// FAZ-10 / K-26 — atomik blok View bileşenleri (saf, Puck'a bağımsız React).
//
// Freebird dersi: TEK render kaynağı. Bu View'ları hem Studio Puck config'i
// (edit-canvas) hem sayfa render'ı (statik ağaç yürüyüşü) AYNEN kullanır →
// editör/önizleme/sayfa aynı çıktıyı verir.
//
// URL alanları BE `UrlRules.BeSafeUrl` aynası `isSafeUrl` ile geçit denetiminden
// geçer; HTML alanları `sanitizeHtml` (DOMPurify, BE Ganss paritesi) ile temizlenir.
//
// FAZ-10.6 / K-31 + K-40 — her görünür blok opsiyonel yapılandırılmış `styleJson`
// taşır: taban bildirimleri inline (token'lardan SONRA merge → stil kazanır),
// tablet/mobile override + `hiddenOn` ise scoped-class media query olarak
// `document.head`'e enjekte edilir (`resolveBlockStyle` + `useHeadStyle`, BE
// breakpoint aynası). Alan YOKKEN çıktı bugünküyle birebir aynıdır.

import type { CSSProperties, ReactNode } from 'react';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { isSafeUrl } from '@shared/utils/safeUrl';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { resolveBlockStyle } from './blockStyle';
import { useHeadStyle } from './useHeadStyle';
import { cx } from './cx';
import {
  clampColumnCount,
  clampSpacerHeight,
  normalizeHeadingLevel,
  type BlockAlign,
  type ButtonSize,
  type ButtonVariant,
  type ContainerAlign,
  type ContainerBackground,
  type ContainerPadding,
  type GridGap,
  type HeadingLevel,
  type HeadingTone,
  type IconName,
  type ImageRounded,
  type ImageWidth,
} from './studioBlocks';
import { ICON_COMPONENTS } from './icons';

function safeUrlOrUndefined(url: string | undefined | null): string | undefined {
  return url && isSafeUrl(url) ? url : undefined;
}

// --- K-29 token → CSS haritaları (tema var'larına çözülür) ---

const HEADING_TONE_STYLE: Record<HeadingTone, CSSProperties> = {
  default: {},
  primary: { color: 'var(--ds-brand, #0a7d55)' },
  muted: { color: 'var(--ds-text-secondary, #64748b)' },
};

const IMAGE_ROUNDED_PX: Record<ImageRounded, number> = { none: 0, small: 8, large: 16 };
const IMAGE_WIDTH_CSS: Record<ImageWidth, string | undefined> = {
  auto: undefined,
  full: '100%',
  half: '50%',
  third: '33.33%',
};

const BUTTON_SIZE_STYLE: Record<ButtonSize, CSSProperties> = {
  small: { padding: '6px 14px', fontSize: 13 },
  medium: { padding: '10px 22px', fontSize: 14 },
  large: { padding: '14px 30px', fontSize: 16 },
};

const CONTAINER_BACKGROUND_STYLE: Record<ContainerBackground, CSSProperties> = {
  none: {},
  light: { background: 'var(--ds-bg-subtle, #f8fafc)' },
  brand: { background: 'var(--ds-brand, #0a7d55)', color: 'var(--ds-text-on-brand, #fff)' },
  dark: { background: 'var(--ds-bg-inverse, #0f172a)', color: 'var(--ds-text-on-inverse, #fff)' },
};

const GRID_GAP_PX: Record<GridGap, number> = { none: 0, small: 8, medium: 16, large: 32 };

// K-39 — grid mobil-yığma: tek global kural (head'e bir kez), tüm grid'ler ≤640'ta
// tek kolona düşer. Kolon sayısından bağımsız (inline grid-template'i ezer).
const GRID_RESPONSIVE_ID = 'cs-grid-responsive';
const GRID_RESPONSIVE_CSS =
  '@media (max-width:640px){.cs-block--grid{grid-template-columns:1fr !important}}';

export function HeadingView({
  text,
  level,
  tone,
  align,
  styleJson,
}: {
  text: string;
  level: HeadingLevel;
  tone?: HeadingTone;
  align?: BlockAlign;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const lvl = normalizeHeadingLevel(level);
  const Tag = `h${lvl}` as const;
  const style: CSSProperties = {
    margin: 0,
    ...(tone ? HEADING_TONE_STYLE[tone] : {}),
    ...(align ? { textAlign: align } : {}),
    ...rs.style,
  };
  return (
    <Tag className={cx('cs-block cs-block--heading', rs.scopedClass)} style={style}>
      {text || '—'}
    </Tag>
  );
}

export function TextView({
  html,
  align,
  styleJson,
}: {
  html: string;
  align?: BlockAlign;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const style: CSSProperties = {
    ...(align ? { textAlign: align } : {}),
    ...rs.style,
  };
  return (
    <div
      className={cx('cs-block cs-block--text', rs.scopedClass)}
      style={Object.keys(style).length > 0 ? style : undefined}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

export function ImageView({
  src,
  alt,
  width,
  rounded,
  styleJson,
}: {
  src: string;
  alt?: string;
  width?: ImageWidth;
  rounded?: ImageRounded;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const safe = safeUrlOrUndefined(src);
  const radius = rounded ? IMAGE_ROUNDED_PX[rounded] : 8;
  const widthCss = width ? IMAGE_WIDTH_CSS[width] : undefined;
  // K-32 — height verilince oran bozulmasın: objectFit cover.
  const objectFit: CSSProperties = rs.style.height ? { objectFit: 'cover' } : {};
  if (!safe) {
    return (
      <div
        className={cx('cs-block cs-block--image cs-block--placeholder', rs.scopedClass)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          borderRadius: radius,
          border: '1px dashed var(--ds-border, #cbd5e1)',
          background: 'var(--ds-surface-2, #f8fafc)',
          color: 'var(--ds-text-secondary, #64748b)',
          fontSize: 13,
          ...(widthCss ? { width: widthCss } : {}),
          ...rs.style,
        }}
      >
        {alt || 'Görsel'}
      </div>
    );
  }
  return (
    <img
      className={cx('cs-block cs-block--image', rs.scopedClass)}
      src={resolveAssetUrl(safe)}
      alt={alt ?? ''}
      loading="lazy"
      style={{
        maxWidth: '100%',
        display: 'block',
        borderRadius: radius,
        ...(widthCss ? { width: widthCss } : {}),
        ...objectFit,
        ...rs.style,
      }}
    />
  );
}

const BUTTON_VARIANT_STYLE: Record<ButtonVariant, CSSProperties> = {
  primary: { background: 'var(--ds-brand, #0a7d55)', color: '#fff', border: 'none' },
  secondary: { background: 'var(--ds-surface-2, #eef2f7)', color: 'inherit', border: 'none' },
  outline: {
    background: 'transparent',
    color: 'var(--ds-brand, #0a7d55)',
    border: '1px solid var(--ds-brand, #0a7d55)',
  },
};

export function ButtonView({
  label,
  href,
  variant,
  size,
  fullWidth,
  icon,
  styleJson,
}: {
  label: string;
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: IconName;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const safe = safeUrlOrUndefined(href);
  const style: CSSProperties = {
    display: fullWidth ? 'flex' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 22px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    cursor: safe ? 'pointer' : 'default',
    ...BUTTON_VARIANT_STYLE[variant ?? 'primary'],
    ...(size ? BUTTON_SIZE_STYLE[size] : {}),
    ...(fullWidth ? { width: '100%' } : {}),
    ...rs.style,
  };
  const text = label || 'Buton';
  const IconCmp = icon ? ICON_COMPONENTS[icon] : undefined;
  const inner = (
    <>
      {IconCmp ? <IconCmp size={16} aria-hidden /> : null}
      {text}
    </>
  );
  return safe ? (
    <a className={cx('cs-block cs-block--button', rs.scopedClass)} href={safe} style={style}>
      {inner}
    </a>
  ) : (
    <span className={cx('cs-block cs-block--button', rs.scopedClass)} role="button" style={style}>
      {inner}
    </span>
  );
}

const CONTAINER_PADDING_PX: Record<ContainerPadding, number> = {
  none: 0,
  small: 12,
  medium: 24,
  large: 40,
};

export function ContainerView({
  align,
  padding,
  background,
  styleJson,
  children,
}: {
  align?: ContainerAlign;
  padding?: ContainerPadding;
  background?: ContainerBackground;
  styleJson?: string;
  children?: ReactNode;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <div
      className={cx('cs-block cs-block--container', rs.scopedClass)}
      style={{
        textAlign: align ?? 'left',
        padding: CONTAINER_PADDING_PX[padding ?? 'medium'],
        ...(background ? CONTAINER_BACKGROUND_STYLE[background] : {}),
        ...rs.style,
      }}
    >
      {children}
    </div>
  );
}

export function GridView({
  columnCount,
  gap,
  styleJson,
  col1,
  col2,
  col3,
  col4,
}: {
  columnCount: number;
  gap?: GridGap;
  styleJson?: string;
  col1?: ReactNode;
  col2?: ReactNode;
  col3?: ReactNode;
  col4?: ReactNode;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  useHeadStyle(GRID_RESPONSIVE_ID, GRID_RESPONSIVE_CSS);
  const count = clampColumnCount(columnCount);
  const columns = [col1, col2, col3, col4].slice(0, count);
  return (
    <div
      className={cx('cs-block cs-block--grid', rs.scopedClass)}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${count}, 1fr)`,
        gap: gap ? GRID_GAP_PX[gap] : 16,
        ...rs.style,
      }}
    >
      {columns.map((col, i) => (
        <div key={i} className="cs-block--grid-col">
          {col}
        </div>
      ))}
    </div>
  );
}

export function SpacerView({ height, styleJson }: { height: number; styleJson?: string }) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <div
      aria-hidden
      className={cx('cs-block cs-block--spacer', rs.scopedClass)}
      style={{ height: clampSpacerHeight(height), ...rs.style }}
    />
  );
}

export function LegacyHtmlView({ html }: { html: string }) {
  // K-20 — salt-okunur legacy köprü içeriği; render-zamanı sanitize (derinlemesine savunma).
  return (
    <div
      className="cs-block cs-block--legacy"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}
